import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { debugLog, debugError, debugWarn } from '../utils/debug';
import { CONTENT_TYPES } from '../constants/content-types';
import { logger } from '../utils/logger';
import { v4 as uuidv4 } from 'uuid';
import { normalizeLibrarianData, getSearchOptimizedData } from '../utils/librarian-utils';

// デバッグモードの判定を行うユーティリティ関数
const isDebugMode = () => window.location.search.includes('debug=true');

// ✅ Phase 2: 業界標準準拠の同期制御監視機能
class SynchronizationMonitor {
  constructor() {
    this.metrics = {
      stepAnalysisCount: 0,
      successCount: 0,
      errorCount: 0,
      averageProcessingTime: 0,
      totalProcessingTime: 0,
      timeoutCount: 0
    };
  }
  
  logSynchronizationEvent(event, data) {
    logger.info(`[Sync Monitor] ${event}`, {
      timestamp: new Date().toISOString(),
      sessionId: data.sessionId,
      success: data.success,
      duration: data.duration,
      error: data.error,
      phase: 'Phase2_Promise_Based_Sync'
    });
    
    this.updateMetrics(data);
  }
  
  updateMetrics(data) {
    this.metrics.stepAnalysisCount++;
    this.metrics.totalProcessingTime += data.duration || 0;
    this.metrics.averageProcessingTime = Math.round(
      this.metrics.totalProcessingTime / this.metrics.stepAnalysisCount
    );
    
    if (data.success) {
      this.metrics.successCount++;
    } else {
      this.metrics.errorCount++;
      if (data.error && data.error.includes('timeout')) {
        this.metrics.timeoutCount++;
      }
    }
  }
  
  getHealthStatus() {
    if (this.metrics.stepAnalysisCount === 0) {
      return { healthy: true, status: 'No data yet' };
    }
    
    const successRate = this.metrics.successCount / this.metrics.stepAnalysisCount;
    return {
      healthy: successRate > 0.95,
      successRate: successRate.toFixed(3),
      totalProcessed: this.metrics.stepAnalysisCount,
      averageTime: this.metrics.averageProcessingTime,
      timeoutRate: (this.metrics.timeoutCount / this.metrics.stepAnalysisCount).toFixed(3)
    };
  }
  
  reset() {
    this.metrics = {
      stepAnalysisCount: 0,
      successCount: 0,
      errorCount: 0,
      averageProcessingTime: 0,
      totalProcessingTime: 0,
      timeoutCount: 0
    };
  }
}

// グローバル監視インスタンス
const syncMonitor = new SynchronizationMonitor();

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [recommendations, setRecommendations] = useState({});
  const [processingStatus, setProcessingStatus] = useState({
    isProcessing: false,
    completed: 0,
    totalTypes: 0,
    stage: null,
    message: null,
    currentType: null
  });
  const [analysisResults, setAnalysisResults] = useState({
    tasteWeaverAnalysis: null,
    librarianAnalysis: null,
    manualInputAnalysis: null
  });
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const sessionIdRef = useRef(null);
  // ★ Step 1-1: 意図的切断フラグ（アイドル管理・サーバータイムアウト等で計画的に切断する場合にtrue）
  const intentionalDisconnectRef = useRef(false);
  
  const SOCKET_URL = process.env.REACT_APP_WS_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = Math.random().toString(36).substring(2, 15);
    }
  }, []);

  const sendTasteWeaverMessage = async (message, messageHistory) => {
    if (!isConnected || !socket) {
      logger.error('TasteWeaver', 'Cannot send message - socket not connected', { 
        isConnected 
      });
      throw new Error('Socket.IO connection is not established');
    }

    const requestId = logger.generateRequestId();
    const messageId = uuidv4(); // ✅ messageId生成
    logger.info('TasteWeaver', 'Sending message', { 
      requestId,
      sessionId: sessionIdRef.current,
      messageLength: message?.length || 0, 
      historyItems: messageHistory?.length || 0,
      messageId // ✅ messageIdをログに追加
    });
    
    return new Promise((resolve, reject) => {
      socket.emit('taste_weaver_message', {
        requestId,
        sessionId: sessionIdRef.current,
        message,
        messageHistory,
        messageId // ✅ messageId送信
      });

      const timeout = setTimeout(() => {
        logger.error('TasteWeaver', 'Response timeout', { requestId, messageId });
        socket.off('taste_weaver_response');
        socket.off('taste_weaver_error');
        reject(new Error('Response timeout'));
      }, 120000); // Step-by-step分析対応のため120秒に延長

      socket.once('taste_weaver_response', (response) => {
        clearTimeout(timeout);
        logger.info('TasteWeaver', 'Received response', { 
          requestId,
          messageId,
          responseLength: typeof response === 'string' ? response.length : 
                         (typeof response === 'object' ? JSON.stringify(response).length : 'unknown')
        });
        
        // デバッグログ（開発環境のみ）
        if (process.env.NODE_ENV === 'development') {
          console.log('WebSocketContext: レスポンス受信:', {
            hasImage: !!response.generatedImage,
            responseKeys: Object.keys(response),
            messageId
          });
        }

        // ✅ 画像生成イベント処理の復元
        if (response && response.generatedImage) {
          // 画像イベントを発行
          window.dispatchEvent(new CustomEvent('image_generated', {
            detail: { generatedImage: response.generatedImage }
          }));

          // デバッグログ（開発環境のみ）
          if (process.env.NODE_ENV === 'development') {
            console.log('WebSocketContext: 画像イベント発行:', {
              imageData: response.generatedImage,
              timestamp: new Date().toISOString(),
              messageId
            });
          }
        }

        // ✅ レスポンスにmessageIdを含めて返す
        resolve({
          ...response,
          messageId
        });
      });

      socket.once('taste_weaver_error', (error) => {
        clearTimeout(timeout);
        logger.error('TasteWeaver', 'Socket error', { 
          requestId,
          messageId,
          error: error.message 
        });
        reject(new Error(error.message));
      });
    });
  };

  // マニュアル入力を送信する関数
  const sendManualInput = useCallback((input, contentTypes) => {
    if (!isConnected || !socket) {
      logger.error('Manual Input', 'Cannot send input - socket not connected', { 
        isConnected 
      });
      return Promise.reject(new Error('WebSocketが接続されていません'));
    }
    
    if (!input) {
      logger.error('Manual Input', 'Empty input');
      return Promise.reject(new Error('入力が空です'));
    }
    
    if (!contentTypes || !Array.isArray(contentTypes) || contentTypes.length === 0) {
      logger.error('Manual Input', 'Invalid content types', { contentTypes });
      return Promise.reject(new Error('コンテンツタイプが指定されていません'));
    }
    
    logger.info('Manual Input', 'Sending input', { 
      inputLength: input.length,
      contentTypes 
    });
    
    const messageId = uuidv4();
    
    // 処理状態を更新
    setProcessingStatus({
      isProcessing: true,
      completed: 0,
      totalTypes: contentTypes.length,
      stage: 'analyzing',
      message: 'マニュアル入力を分析中...',
      currentType: null
    });
    
    // マニュアル入力を送信
    socket.emit('content_recommendation', {
      messageId,
      inputType: 'manual_input',
      manualInput: input,
      contentTypes
    });
    
    return messageId;
  }, [isConnected, socket]);

  const cleanupSocket = useCallback(() => {
    if (socket) {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('error');
      socket.off('taste_weaver_response');
      socket.off('taste_weaver_error');
      socket.close();
      setSocket(null);
    }
  }, []);

  // SSEイベントを処理する関数を定義
  const handleRecommendationEvent = (data) => {
    logger.debug('Recommendation', 'Handling event', { 
      type: data.type, 
      contentType: data.contentType 
    });

    switch (data.type) {
      case 'init':
        setProcessingStatus({
          isProcessing: true,
          totalTypes: data.contentTypes?.length || 0,
          completed: 0,
          error: null,
          stage: 'preparing',
          message: data.message || '推薦プロセスを開始しました'
        });
        break;

      case 'content_start':
        setProcessingStatus(prev => ({
          ...prev,
          stage: 'processing',
          currentType: data.contentType,
          message: data.message
        }));
        break;

      case 'progress':
        setProcessingStatus(prev => ({
          ...prev,
          stage: 'processing',
          message: data.message,
          currentType: data.contentType
        }));
        break;

      case 'content_complete':
        setProcessingStatus(prev => ({
          ...prev,
          completed: prev.completed + 1,
          isProcessing: (prev.completed + 1) < prev.totalTypes,
          message: data.message
        }));
        break;

      case 'contentRecommendation':
        if (data.contentType && data.recommendations) {
          logger.info('Recommendation', 'Received recommendations', {
            contentType: data.contentType,
            count: data.recommendations.length
          });
          
          setRecommendations(prev => ({
            ...prev,
            [data.contentType]: data.recommendations
          }));
        }
        break;

      case 'content_empty':
        setProcessingStatus(prev => ({
          ...prev,
          completed: prev.completed + 1,
          isProcessing: (prev.completed + 1) < prev.totalTypes,
          message: data.message
        }));
        setRecommendations(prev => ({
          ...prev,
          [data.contentType]: []
        }));
        break;

      case 'content_error':
        setProcessingStatus(prev => ({
          ...prev,
          completed: prev.completed + 1,
          isProcessing: (prev.completed + 1) < prev.totalTypes,
          error: data.error || 'エラーが発生しました',
          message: data.message
        }));
        break;

      case 'complete':
        setProcessingStatus(prev => ({
          ...prev,
          isProcessing: false,
          stage: 'complete',
          message: data.message || '推薦プロセスが完了しました'
        }));
        break;

      default:
        logger.warn('Recommendation', 'Unknown event type', { 
          type: data.type 
        });
        break;
    }
  };










  useEffect(() => {
    const connectSocket = () => {
      // 既存のソケットをクリーンアップ
      if (socket) {
        cleanupSocket();
      }
      
      console.log(`Connecting to WebSocket at ${SOCKET_URL}...`);
      
      // 認証モードに応じた接続設定
      const password = localStorage.getItem('auth-password');
      const useSNSAuth = !password; // パスワードがなければSNS認証モード
      
      // 新しいソケット接続を作成
      const newSocket = io(SOCKET_URL, {
        // 認証設定: SNS認証時はCookieベース、従来型はパスワードベース
        ...(useSNSAuth ? {} : { auth: { password: password } }),
        withCredentials: true,                // Cookie送信を有効化（SNS認証用）
        transports: ['websocket', 'polling'], // ✅ pollingフォールバック追加
        upgrade: true,                        // ✅ upgrade有効化
        reconnection: true,
        reconnectionAttempts: 10,             // ✅ 再接続回数増加
        reconnectionDelay: 2000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        // ✅ ping/pong設定をバックエンドと統一
        pingTimeout: 60000,                   // 60秒でタイムアウト（バックエンドと統一）
        pingInterval: 25000,                  // 25秒間隔でping（バックエンドと統一）
        query: {
          sessionId: sessionIdRef.current
        }
      });
      
      // 認証エラーハンドラーを追加
      newSocket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error.message);
        
        // 認証エラーの場合はログアウト処理
        if (error.message === 'Authentication error') {
          console.error('WebSocket authentication failed. Redirecting to login...');
          // localStorageのクリーンアップ（従来型認証の場合）
          localStorage.removeItem('auth-password');
          localStorage.removeItem('auth-timestamp');
          window.location.reload();
        }
      });
      
      // ★ Step 0-2: connectSocket内のconnectハンドラーを簡素化
      // 詳細な監視ロジックは2つ目のuseEffect内のhandleConnectに集約
      newSocket.on('connect', () => {
        console.log('WebSocket Connected!', newSocket.id);
        setIsConnected(true);
        setReconnectAttempt(0);
        // セッション登録はここのみで行う
        newSocket.emit('register_session', {
          sessionId: sessionIdRef.current
        });
      });
      
      // ★ Step 0-2: connectSocket内のdisconnectハンドラーは削除
      // 2つ目のuseEffect内のhandleDisconnectに一本化
      
      newSocket.on('error', (error) => {
        console.error('WebSocket Error:', error);
        setError(`WebSocketエラー: ${error.message || 'Unknown error'}`);
      });
      
      // 接続を保存
      setSocket(newSocket);
    };
    
    // 初期接続
    connectSocket();
    
    // クリーンアップ
    return () => {
      cleanupSocket();
    };
  }, []);
  
  // ★ Step 0-1: カスタムheartbeatは廃止（Socket.IO ping/pong で代替）
  // Socket.IO は pingInterval: 25000, pingTimeout: 60000 で自動的にping/pongを行う
  // カスタムheartbeatは帯域を無駄に消費していたため削除

  useEffect(() => {
    if (!socket) return;

    // Phase P++B-1: 変数スコープ管理
    let connectionMonitor;

    const handleConnect = () => {
      console.log('WebSocket connected.');
      setIsConnected(true);
      
      // ★ Step 0-5: onAnyリスナー累積防止
      // 再接続のたびにhandleConnectが呼ばれるため、
      // 先に既存のonAnyリスナーを除去してから再登録する。
      socket.offAny();
      
      // ★ Step 0-6: 既存のconnectionMonitorをクリアしてから再起動
      // 再接続時に古いsetIntervalが残り続ける問題を防止
      if (connectionMonitor) {
        clearInterval(connectionMonitor);
        connectionMonitor = null;
      }
      
      // ✅ Phase P++B-1: WebSocket通信完全監視開始
      console.log('Phase P++B-1: WebSocket通信完全監視開始', {
        socketId: socket.id,
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current
      });
      
      // 全WebSocketイベント監視
      socket.onAny((eventName, ...args) => {
        const eventData = args[0] || {};
        console.log('📡 Phase P++B-1: WebSocketイベント受信', {
          eventName: eventName,
          timestamp: new Date().toISOString(),
          socketId: socket.id,
          sessionId: sessionIdRef.current,
          hasData: args.length > 0,
          dataKeys: typeof eventData === 'object' ? Object.keys(eventData) : [],
          messageId: eventData.messageId || 'N/A',
          isTargetEvent: eventName === 'integrated_step_analysis_complete'
        });
        
        // integrated_step_analysis_complete専用追跡（Phase P++A統合）
        if (eventName === 'integrated_step_analysis_complete') {
          console.log('🎯 Phase P++B-1: integrated_step_analysis_complete専用追跡', {
            timestamp: new Date().toISOString(),
            socketId: socket.id,
            sessionId: sessionIdRef.current,
            messageId: eventData.messageId,
            hasLibrarianAnalysis: !!eventData.librarianAnalysis,
            hasSteps: !!eventData.librarianAnalysis?.steps,
            stepCount: eventData.librarianAnalysis?.steps ? Object.keys(eventData.librarianAnalysis.steps).length : 0,
            eventDataKeys: Object.keys(eventData),
            // Phase P++A情報も統合
            stage: eventData.stage,
            source: eventData.source,
            isComplete: eventData.isComplete
          });
        }
      });
      
      // WebSocket接続状況継続監視
      connectionMonitor = setInterval(() => {
        console.log('🔍 Phase P++B-1: WebSocket接続状況監視', {
          timestamp: new Date().toISOString(),
          socketId: socket.id,
          connected: socket.connected,
          disconnected: socket.disconnected,
          sessionId: sessionIdRef.current,
          transportType: socket.io.engine?.transport?.name || 'unknown'
        });
      }, 10000);
    };

    // ★ Step 1-2: disconnectハンドラー改修（intentionalDisconnectRefで計画的切断を区別）
    const handleDisconnect = (reason) => {
      console.log(`WebSocket disconnected: ${reason}`);

      // 意図的切断（アイドル管理やクリーンアップ）の場合はエラー表示を抑制
      if (intentionalDisconnectRef.current) {
        console.log('Intentional disconnect - suppressing error state', { reason });
        setIsConnected(false);
        // Phase P++B-1: 接続監視クリーンアップ
        if (connectionMonitor) {
          clearInterval(connectionMonitor);
          connectionMonitor = null;
        }
        return; // これ以上の処理をスキップ
      }

      setIsConnected(false);
      
      // Phase P++B-1: 接続監視クリーンアップ
      if (connectionMonitor) {
        clearInterval(connectionMonitor);
        connectionMonitor = null;
      }
    };

    // ★ Step 1-5: timeout_warning ハンドラー追加
    // サーバー側の15分タイムアウト → 5秒後に強制切断のフローをエラー表示なしで処理
    const handleTimeoutWarning = (data) => {
      console.log('Server timeout warning received:', data);
      // サーバー主導のタイムアウトも「意図的切断」として扱い、エラー表示を抑制
      intentionalDisconnectRef.current = true;
    };

    const handleChatMessage = (data) => {
      console.log('Chat message received:', data);
      setMessages((prev) => [...prev, { ...data, timestamp: new Date() }]);
    };

    const handleImageGeneration = (data) => {
      // 詳細なデバッグ情報をコンソールに出力
      const isDebugMode = window.location.search.includes('debug=true');
      console.log('🖼️ image_generated イベント受信:', {
        timestamp: new Date().toISOString(),
        dataKeys: Object.keys(data),
        hasImageUrl: !!data.imageUrl,
        hasLocalPath: !!data.localPath,
        isDebugMode
      });
      
      // データ検証
      if (!data) {
        console.error('❌ image_generated: データが空です');
        return;
      }
      
      // 複数のデータ形式に対応
      let normalizedData = { ...data };
      
      // generatedImageプロパティ内にデータがある場合は展開
      if (data.generatedImage && typeof data.generatedImage === 'object') {
        normalizedData = {
          ...normalizedData,
          ...data.generatedImage,
          _originalFormat: 'nested' // データ追跡用
        };
      }
      
      // 最低限必要なデータの検証
      if (!normalizedData.imageUrl && !normalizedData.localPath) {
        console.error('❌ image_generated: 画像URLとローカルパスの両方がありません', normalizedData);
        return;
      }

      // 受信したデータからの情報収集
      const imageInfo = {
        imageUrl: normalizedData.imageUrl,
        localPath: normalizedData.localPath,
        originalPath: normalizedData.originalPath,
        backupUrls: Array.isArray(normalizedData.backupUrls) ? normalizedData.backupUrls : [],
        prompt: normalizedData.prompt,
        timestamp: normalizedData.timestamp || Date.now(),
        sessionId: normalizedData.sessionId || sessionIdRef.current
      };
      
      if (isDebugMode) {
        console.log('📊 正規化された画像情報:', {
          imageUrl: imageInfo.imageUrl,
          localPath: imageInfo.localPath,
          hasBackupUrls: imageInfo.backupUrls.length > 0,
          promptPreview: imageInfo.prompt ? imageInfo.prompt.substring(0, 50) + '...' : 'なし',
          timestamp: new Date(imageInfo.timestamp).toISOString()
        });
      }
      
      // URLの検証と正規化
      if (imageInfo.imageUrl) {
        // URL形式の検証
        if (!isValidUrl(imageInfo.imageUrl)) {
          console.warn('⚠️ 画像URLが有効なURL形式ではありません:', imageInfo.imageUrl);
          
          // バックエンドURLと組み合わせて修正を試みる
          if (imageInfo.imageUrl.startsWith('/') && !imageInfo.imageUrl.startsWith('//')) {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
            imageInfo.imageUrl = `${backendUrl}${imageInfo.imageUrl}`;
            console.log('🔄 画像URLを修正しました:', imageInfo.imageUrl);
          }
        }
        
        // 画像URLの存在確認
        fetch(imageInfo.imageUrl, { method: 'HEAD' })
          .then(response => {
            console.log('🔍 画像URL確認結果:', {
              url: imageInfo.imageUrl,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries([...response.headers]),
              timestamp: new Date().toISOString()
            });
          })
          .catch(err => {
            console.error('❌ 画像URL確認エラー:', {
              url: imageInfo.imageUrl,
              error: err.message,
              timestamp: new Date().toISOString()
            });
          });
      }
      
      // ローカルパスの検証(バックエンドからのパス)
      if (imageInfo.localPath) {
        let fullLocalUrl = imageInfo.localPath;
        
        // 相対パスの場合は絶対パスに変換
        if (fullLocalUrl.startsWith('/') && !fullLocalUrl.startsWith('//')) {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
          fullLocalUrl = `${backendUrl}${fullLocalUrl}`;
        }
        
        // ローカルパスのURLの存在確認
        fetch(fullLocalUrl, { method: 'HEAD' })
          .then(response => {
            console.log('🔍 ローカルパスURL確認結果:', {
              url: fullLocalUrl,
              status: response.status,
              statusText: response.statusText,
              timestamp: new Date().toISOString()
            });
          })
          .catch(err => {
            console.error('❌ ローカルパスURL確認エラー:', {
              url: fullLocalUrl,
              error: err.message,
              timestamp: new Date().toISOString()
            });
          });
      }
      
      // バックアップURLの検証
      if (imageInfo.backupUrls && imageInfo.backupUrls.length > 0) {
        imageInfo.backupUrls.forEach((backupUrl, index) => {
          if (backupUrl && typeof backupUrl === 'string') {
            // URL形式の検証と修正
            let normalizedBackupUrl = backupUrl;
            if (backupUrl.startsWith('/') && !backupUrl.startsWith('//')) {
              const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
              normalizedBackupUrl = `${backendUrl}${backupUrl}`;
              console.log(`🔄 バックアップURL(${index})を修正しました:`, normalizedBackupUrl);
            }
            
            // 存在確認
            fetch(normalizedBackupUrl, { method: 'HEAD' })
              .then(response => {
                console.log(`🔍 バックアップURL(${index})確認結果:`, {
                  url: normalizedBackupUrl,
                  status: response.status,
                  statusText: response.statusText,
                  timestamp: new Date().toISOString()
                });
              })
              .catch(err => {
                console.warn(`⚠️ バックアップURL(${index})確認エラー:`, {
                  url: normalizedBackupUrl,
                  error: err.message,
                  timestamp: new Date().toISOString()
                });
              });
          }
        });
      }
      
      // グローバルフラグを設定して追跡（デバッグ用）
      window.__LAST_IMAGE_GENERATED = {
        timestamp: new Date().toISOString(),
        rawData: data,
        normalizedData: normalizedData,
        imageInfo: {
          url: imageInfo.imageUrl,
          localPath: imageInfo.localPath,
          hasPrompt: !!imageInfo.prompt,
          hasBackupUrls: imageInfo.backupUrls.length > 0
        }
      };
      
      console.log('📢 image_generated カスタムイベントを発火します');
      
      // 複数のイベント形式で発火して最大限の互換性を確保
      // 1. 標準形式のイベント発火 (元データをそのまま渡す)
      window.dispatchEvent(new CustomEvent('image_generated', {
        detail: data
      }));
      
      // 2. 正規化データでのイベント発火
      window.dispatchEvent(new CustomEvent('image_generated_normalized', {
        detail: imageInfo
      }));
      
      // 3. 互換性のために古いイベント名でも発火
      window.dispatchEvent(new CustomEvent('imageGeneration', {
        detail: normalizedData
      }));
      
      console.log('✅ image_generated イベント発火完了');
    };

    const handleProgressUpdate = (data) => {
      console.log('Progress update received:', data);
      setProcessingStatus(prev => ({
        ...prev,
        stage: 'processing',
        progress: data.progress,
        message: data.message,
        currentType: data.contentType
      }));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('timeout_warning', handleTimeoutWarning);
    socket.on('recommendation_progress', handleProgressUpdate);

    // ✅ image_generation_statusイベントハンドラー追加
    const handleImageGenerationStatus = (data) => {
      console.log('🔔 image_generation_status受信:', {
        status: data.status,
        message: data.message,
        phase: data.phase,
        timestamp: new Date().toISOString()
      });
      
      // 状態別の処理
      if (data.status === 'preparing') {
        // 画像生成準備中の通知を表示
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.message,
          timestamp: Date.now(),
          type: 'system_notification'
        }]);
      } else if (data.status === 'generating') {
        // 画像生成中の通知を表示
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.message,
          timestamp: Date.now(),
          type: 'system_notification'
        }]);
      } else if (data.status === 'failed') {
        // 画像生成失敗の通知を表示
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.message,
          timestamp: Date.now(),
          type: 'error_notification'
        }]);
      }
      
      // CustomEventとして発火してコンポーネントに通知
      // Phase 7C: 重複表示防止のため、CustomEvent発火を無効化
      // window.dispatchEvent(new CustomEvent('image_generation_status', {
      //   detail: data
      // }));
    };

    socket.on('image_generation_status', handleImageGenerationStatus);

    // ✅ Phase 7: organizing開始通知
    socket.on('organizing_started', (data) => {
      console.log('🎯 Phase 7 DEBUG: organizing_started received:', {
        sessionId: data.sessionId,
        messageLength: data.message?.length,
        stage: data.stage,
        timestamp: data.timestamp,
        socketId: socket.id,
        receivedAt: new Date().toISOString(),
        data: data
      });
      
      setMessages(prev => {
        const newMessage = {
          role: 'system',
          content: data.message,
          timestamp: data.timestamp,
          type: 'organizing_start'
        };
        console.log('🎯 Phase 7 DEBUG: Adding organizing_start message to state:', newMessage);
        return [...prev, newMessage];
      });
    });

    // organizing 完了結果は HTTP 応答 (会話バブル) が単一の正として描画する。
    // 文章化開始の即時 status は organizing_started、テーマ抽出への遷移は
    // ChatInterface の isComplete 分岐が担うため、organizing_completed は購読しない
    // (購読すると整理ブロックが二重描画され、ノイズ status が挟まる)。

    // ✅ step_analysis_completedイベントハンドラー（Phase0修正 + Phase11拡張）
    socket.on('step_analysis_completed', (responseData) => {
      console.log('🔧 Phase0+11: step_analysis_completed received:', {
        messageId: responseData.messageId,
        hasAnalysisResults: !!responseData.analysisResults,
        hasSteps: !!responseData.analysisResults?.steps,
        stepCount: responseData.analysisResults?.steps ? Object.keys(responseData.analysisResults.steps).length : 0,
        hasRecommendationStartMessage: !!responseData.recommendationStartMessage,
        recommendationStartMessage: responseData.recommendationStartMessage,
        timestamp: new Date().toISOString()
      });
      
      try {
        if (responseData.messageId && responseData.analysisResults) {
          // P-1 Step 2b: messageId 別 slot に加えて top-level librarianAnalysis にも sync する。
          // Book/index.jsx:getRecommendations は analysisResults?.librarianAnalysis を fallback
          // として参照しており、ここで sync しないと chat callback (override) 経由以外で
          // 常に null になり librarianAnalysis を request body に同梱できない。
          setAnalysisResults(prev => ({
            ...prev,
            [responseData.messageId]: responseData.analysisResults,
            librarianAnalysis: responseData.analysisResults
          }));

          console.log('✅ Phase0: analysisResults updated via step_analysis_completed:', {
            messageId: responseData.messageId,
            hasSteps: !!responseData.analysisResults.steps
          });
        }
        
        // 🆕 Phase 11: recommendationStartMessageをLibrarianStepViewに送信
        if (responseData.recommendationStartMessage) {
          // LibrarianStepViewの完全な準備完了を待つ
          setTimeout(() => {
            const customEvent = new CustomEvent('recommendationStartMessage', {
              detail: {
                messageId: responseData.messageId,
                recommendationStartMessage: responseData.recommendationStartMessage
              }
            });
            window.dispatchEvent(customEvent);
            
            console.log('✅ Phase11: recommendationStartMessage dispatched (500ms delay):', {
              messageId: responseData.messageId,
              message: responseData.recommendationStartMessage
            });
          }, 500);
        }
      } catch (error) {
        console.error('❌ Phase0+11: Error handling step_analysis_completed:', error);
      }
    });

    // ✅ integrated_step_analysis_completeイベントハンドラー（分析結果のみ処理）
    socket.on('integrated_step_analysis_complete', (responseData) => {
      console.log('🔍 integrated_step_analysis_complete受信:', {
        messageId: responseData.messageId,
        hasLibrarianAnalysis: !!responseData.librarianAnalysis,
        hasSteps: !!responseData.librarianAnalysis?.steps,
        stepCount: responseData.librarianAnalysis?.steps ? Object.keys(responseData.librarianAnalysis.steps).length : 0,
        timestamp: new Date().toISOString()
      });
      
      try {
        // analysisResults設定のみ（開始メッセージは削除）
        if (responseData.messageId && responseData.librarianAnalysis) {
          // P-1 Step 2b: step_analysis_completed と同じく top-level slot にも sync する。
          setAnalysisResults(prev => ({
            ...prev,
            [responseData.messageId]: responseData.librarianAnalysis,
            librarianAnalysis: responseData.librarianAnalysis
          }));

          console.log('✅ analysisResults設定完了:', {
            messageId: responseData.messageId,
            hasSteps: !!responseData.librarianAnalysis.steps
          });
        }
      } catch (error) {
        console.error('Error handling integrated_step_analysis_complete:', error);
      }
    });

    // グローバルにイベントリスナーをデバッグできるようにする
    if (!window._debugListeners) {
      window._debugListeners = {};
    }

    return () => {
      // Phase P++B-1: connectionMonitorクリーンアップ
      if (connectionMonitor) {
        clearInterval(connectionMonitor);
        connectionMonitor = null;
      }
      
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('timeout_warning', handleTimeoutWarning);
      socket.off('recommendation_progress', handleProgressUpdate);
      // ✅ image_generation_statusクリーンアップ
      socket.off('image_generation_status', handleImageGenerationStatus);
      socket.off('organizing_started');
      // ✅ Phase0: step_analysis_completedクリーンアップ
      socket.off('step_analysis_completed');
      // ✅ integrated_step_analysis_completeクリーンアップ
      socket.off('integrated_step_analysis_complete');
    };
  }, [socket]);

  // URL検証関数
  const isValidUrl = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // ✅ Phase 2: Promise-based同期制御への拡張（業界標準アップグレード）
  const ensureStepAnalysisComplete = async (stepAnalysisResults) => {
    const startTime = performance.now();
    
    return new Promise((resolve, reject) => {
      // 業界標準のタイムアウト設定（5秒）
      const timeout = setTimeout(() => {
        const duration = performance.now() - startTime;
        logger.error('❌ Step analysis completion timeout', {
          duration,
          timeout: 5000,
          timestamp: new Date().toISOString()
        });
        
        // 業界標準監視機能でのタイムアウト記録
        syncMonitor.logSynchronizationEvent('Step Analysis Timeout', {
          sessionId: sessionIdRef.current,
          success: false,
          duration: Math.round(duration),
          error: 'Step analysis completion timeout'
        });
        
        reject(new Error('Step analysis completion timeout'));
      }, 5000);
      
      try {
        // Phase 1の基本検証を維持
        if (!stepAnalysisResults || typeof stepAnalysisResults !== 'object') {
          clearTimeout(timeout);
          reject(new Error('Invalid step analysis results format'));
          return;
        }
        
        if (!stepAnalysisResults.steps) {
          clearTimeout(timeout);
          reject(new Error('Missing steps field in step analysis results'));
          return;
        }
        
        if (!stepAnalysisResults.analysis) {
          clearTimeout(timeout);
          reject(new Error('Missing analysis field in step analysis results'));
          return;
        }
        
        if (!stepAnalysisResults.keywords) {
          clearTimeout(timeout);
          reject(new Error('Missing keywords field in step analysis results'));
          return;
        }
        
        // Promise-based同期制御の確実な完了保証
        const duration = performance.now() - startTime;
        
        // 検証成功 + 同期制御完了ログ
        logger.info('✅ Step-by-step analysis validation & synchronization complete', {
          hasSteps: !!stepAnalysisResults.steps,
          hasAnalysis: !!stepAnalysisResults.analysis,
          hasKeywords: !!stepAnalysisResults.keywords,
          stepCount: stepAnalysisResults.steps ? Object.keys(stepAnalysisResults.steps).length : 0,
          processingDuration: Math.round(duration),
          phase: 'Phase2_Promise_Based_Sync',
          timestamp: new Date().toISOString()
        });
        
        // 業界標準監視機能での成功記録
        syncMonitor.logSynchronizationEvent('Step Analysis Success', {
          sessionId: sessionIdRef.current,
          success: true,
          duration: Math.round(duration),
          error: null
        });
        
        clearTimeout(timeout);
        
        // 推薦プロセス開始の準備完了を確実に通知
        console.log('🔄 Step analysis validated and synchronized, ready for recommendations');
        resolve(stepAnalysisResults);
        
      } catch (error) {
        clearTimeout(timeout);
        const duration = performance.now() - startTime;
        
        logger.error('❌ Step-by-step analysis validation & synchronization failed', {
          error: error.message,
          duration: Math.round(duration),
          receivedData: stepAnalysisResults,
          phase: 'Phase2_Promise_Based_Sync',
          timestamp: new Date().toISOString()
        });
        
        // 業界標準監視機能でのエラー記録
        syncMonitor.logSynchronizationEvent('Step Analysis Error', {
          sessionId: sessionIdRef.current,
          success: false,
          duration: Math.round(duration),
          error: error.message
        });
        
        reject(error);
      }
    });
  };

  // ============================================================
  // ★ Step 3-3: processingStatus の Ref ラッパー（stale closure 対策）
  // ============================================================
  const processingStatusRef = useRef(processingStatus);

  // processingStatus が変更されるたびに Ref を更新
  useEffect(() => {
    processingStatusRef.current = processingStatus;
  }, [processingStatus]);

  // ============================================================
  // ★ Step 3-1: Page Visibility ベースのアイドル管理
  // ============================================================
  const disconnectTimerRef = useRef(null);
  const IDLE_DISCONNECT_DELAY = 5 * 60 * 1000; // 5分
  const IDLE_DISCONNECT_ACK_TIMEOUT = 3000; // ack待機タイムアウト: 3秒

  useEffect(() => {
    if (!socket) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 処理中チェック（processingStatusRef経由でstale closure対策）
        if (processingStatusRef.current.isProcessing) {
          console.log('Tab hidden but processing in progress - skipping idle disconnect');
          return;
        }

        // タブがバックグラウンドに → 5分後に切断予約
        console.log('Tab hidden - scheduling disconnect in 5 minutes');
        disconnectTimerRef.current = setTimeout(() => {
          // ★ Step 3-1 v2.2: タイマー発火時にも処理中チェックを再実行
          if (processingStatusRef.current.isProcessing) {
            console.log('Idle disconnect timer fired but processing in progress - aborting');
            disconnectTimerRef.current = null;
            return;
          }

          if (socket && socket.connected) {
            console.log('Idle disconnect: disconnecting WebSocket');
            intentionalDisconnectRef.current = true;

            // Socket.IOの自動再接続を一時的に無効化
            socket.io.opts.reconnection = false;

            // ★ Acknowledgmentパターンでサーバーへの到達を保証
            socket.emit('idle_disconnect', {
              sessionId: sessionIdRef.current
            }, () => {
              // サーバーがackを返した → 安全に切断
              console.log('idle_disconnect acknowledged by server - disconnecting');
              socket.disconnect();
            });

            // バックアップ: ack が返らない場合は3秒後に強制切断
            setTimeout(() => {
              if (socket.connected) {
                console.log('idle_disconnect ack timeout - force disconnecting');
                socket.disconnect();
              }
            }, IDLE_DISCONNECT_ACK_TIMEOUT);
          }
        }, IDLE_DISCONNECT_DELAY);

      } else {
        // タブがフォアグラウンドに復帰
        console.log('Tab visible - cancelling disconnect timer or reconnecting');

        // 切断タイマーが残っていればキャンセル（5分以内に復帰した場合）
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
          console.log('Disconnect timer cancelled - still connected');
          return;
        }

        // 既に切断済みの場合は再接続
        if (socket && !socket.connected) {
          console.log('Reconnecting WebSocket after idle disconnect');
          intentionalDisconnectRef.current = false;
          socket.io.opts.reconnection = true; // 自動再接続を再有効化
          socket.connect();
          // ※ connect後、既存の 'connect' ハンドラーで register_session が自動送信される
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [socket]);

  // ★ 新規セッション切替時のステートリセット関数
  const resetSession = useCallback(() => {
    console.log('resetSession: Clearing all session state for new session', {
      timestamp: new Date().toISOString(),
      sessionId: sessionIdRef.current  // sessionIdRef はリセットしない（WebSocket接続の整合性を維持）
    });
    setMessages([]);
    setAnalysisResults({
      tasteWeaverAnalysis: null,
      librarianAnalysis: null,
      manualInputAnalysis: null
    });
    setRecommendations({});
    setProcessingStatus({
      isProcessing: false,
      completed: 0,
      totalTypes: 0,
      stage: null,
      message: null,
      currentType: null
    });
  }, []);

  // ★ Step 3-5: reconnect 関数の定義
  const reconnect = useCallback(() => {
    if (socket?.connected) return;

    intentionalDisconnectRef.current = false;
    socket.io.opts.reconnection = true;
    socket.connect();
  }, [socket]);

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        isConnected,
        error,
        sendTasteWeaverMessage,
        sendManualInput,
        reconnectAttempt,
        maxReconnectAttempts: 5,
        recommendations,
        processingStatus,
        analysisResults,
        // 🎯 Phase 7: メッセージ状態を提供
        messages,
        setMessages,
        sessionId: sessionIdRef.current,
        // ★ Step 1-4: intentionalDisconnectRef を公開（ChatInterface等が参照可能）
        intentionalDisconnectRef,
        // ★ 新規セッション切替時のステートリセット関数を公開
        resetSession,
        // ★ Step 3-5: reconnect関数を公開
        reconnect,
        // Phase 2: 業界標準監視機能を公開
        getSyncMonitorHealth: () => syncMonitor.getHealthStatus(),
        resetSyncMonitor: () => syncMonitor.reset()
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketProvider; 